import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowRight, Camera, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Avatar, Button, Input, Textarea } from "../../../components/ui";
import { ImageCropModal } from "../../../components/ui/ImageCropModal";
import { useSession, type AppSessionUser } from "../../../lib/auth-client";
import { trpc } from "../../../lib/trpc";
import { uploadFile } from "../../../lib/uploadFile";
import type { CropData } from "../../../lib/utils";

export const Route = createFileRoute("/app/onboarding/step-1")({
  component: ProfileSetupStep,
});

function ProfileSetupStep() {
  const navigate = useNavigate();
  const { data: session, refetch: refetchSession } = useSession();
  const user = session?.user as AppSessionUser | undefined;

  const [displayName, setDisplayName] = useState("");
  const [profileHandle, setProfileHandle] = useState("");
  const [bio, setBio] = useState("");

  // Avatar state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingAvatarBlob, setPendingAvatarBlob] = useState<Blob | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setDisplayName(user?.name ?? "");
    setProfileHandle(user?.slug ?? "");
    setBio(user?.bio ?? "");
  }, [user?.bio, user?.name, user?.slug]);

  const updateProfile = trpc.user.updateProfile.useMutation();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ["image/png", "image/jpeg", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error("Unsupported file type. Please use PNG, JPEG, or WebP.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("File is too large. Maximum size is 2 MB.");
      return;
    }

    const url = URL.createObjectURL(file);
    setCropSrc(url);
    setCropOpen(true);
    e.target.value = "";
  };

  const handleCropConfirm = (result: { blob: Blob; cropData: CropData }) => {
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarPreview(URL.createObjectURL(result.blob));
    setPendingAvatarBlob(result.blob);
    setCropOpen(false);
    setCropSrc(null);
  };

  const handleNext = async () => {
    const trimmedDisplayName = displayName.trim();
    const trimmedHandle = profileHandle.trim();
    const trimmedBio = bio.trim();

    if (!trimmedDisplayName) {
      toast.error("Display name is required.");
      return;
    }
    if (!trimmedHandle) {
      toast.error("Profile URL is required.");
      return;
    }

    setIsSaving(true);
    try {
      const profileData: Record<string, unknown> = {
        name: trimmedDisplayName,
        slug: trimmedHandle,
        bio: trimmedBio || undefined,
      };

      // Upload avatar if one was cropped
      if (pendingAvatarBlob && user) {
        const { publicUrl } = await uploadFile(pendingAvatarBlob, "user-avatar", user.id);
        profileData.avatarUrl = publicUrl ?? undefined;
      }

      await updateProfile.mutateAsync(profileData as Parameters<typeof updateProfile.mutate>[0]);
      await refetchSession({ query: { disableCookieCache: true } });
      void navigate({ to: "/app/onboarding/step-2" });
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to update profile. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const previewName = displayName.trim() || user?.name || "Player";
  const previewHandle = profileHandle.trim() || user?.slug || "";
  const currentAvatar = avatarPreview ?? user?.image ?? null;
  const isPending = isSaving || updateProfile.isPending;

  return (
    <div className="animate-fade-up p-4 md:p-6">
      {/* Card */}
      <div className="relative rounded-2xl bg-surface-900/80 border border-surface-700/50 overflow-hidden">
        {/* Top accent */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-500/40 to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(38,217,181,0.04)_0%,_transparent_60%)] pointer-events-none" />

        <div className="relative px-6 sm:px-8 py-8 space-y-8">
          {/* Header */}
          <div className="text-center space-y-2">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Set Up Your Profile</h1>
            <p className="text-sm text-surface-400">Let other players know who you are</p>
          </div>

          {/* Avatar + name display */}
          <div className="flex flex-col items-center gap-4">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="group relative cursor-pointer"
              disabled={isPending}
            >
              <div className="absolute -inset-2 rounded-full bg-brand-500/10 blur-xl transition-opacity group-hover:opacity-100 opacity-60" />
              <Avatar
                src={currentAvatar}
                alt={previewName}
                initials={previewName.charAt(0)}
                size="xl"
                className="ring-2 ring-surface-700 group-hover:ring-brand-500/50 transition-all"
              />
              {/* Hover overlay */}
              <div className="absolute inset-0 flex items-center justify-center rounded-full bg-surface-900/60 opacity-0 group-hover:opacity-100 transition-opacity">
                <Camera className="size-5 text-white" />
              </div>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={handleFileSelect}
            />
            <div className="text-center">
              <p className="font-bold text-xl text-white">{previewName}</p>
              <p className="text-xs text-surface-500 mt-0.5">
                {previewHandle ? `/users/${previewHandle}` : "Choose your profile URL"}
              </p>
            </div>
          </div>

          <div className="grid gap-4">
            <Input
              label="Display Name"
              id="display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="What should other players call you?"
              autoComplete="nickname"
              required
            />

            <div className="space-y-1.5">
              <Input
                label="Profile URL"
                id="profile-handle"
                value={profileHandle}
                onChange={(e) => setProfileHandle(e.target.value)}
                placeholder="apoc"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                required
              />
              <p className="text-xs text-surface-500">
                This becomes your permanent public profile URL. You can change it during onboarding,
                but not later.
              </p>
            </div>
          </div>

          {/* Bio input */}
          <Textarea
            label="Bio"
            placeholder="Tell us about yourself... (optional)"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            maxLength={500}
            rows={3}
            resize="none"
          />
          <p className="text-xs text-surface-500 -mt-6 text-right">{bio.length}/500</p>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2">
            <Button
              variant="ghost"
              onClick={() => void navigate({ to: "/app/onboarding/step-2" })}
              disabled={isPending}
            >
              Skip
            </Button>
            <Button
              variant="primary"
              onClick={() => void handleNext()}
              disabled={isPending}
              rightIcon={
                isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <ArrowRight className="size-4" />
                )
              }
            >
              {isPending ? "Saving..." : "Next"}
            </Button>
          </div>
        </div>
      </div>

      {/* Crop modal */}
      {cropSrc && (
        <ImageCropModal
          open={cropOpen}
          onClose={() => {
            setCropOpen(false);
            setCropSrc(null);
          }}
          imageSrc={cropSrc}
          aspect={1}
          shape="round"
          outputWidth={256}
          outputHeight={256}
          onCropConfirm={handleCropConfirm}
          initialCropData={null}
        />
      )}
    </div>
  );
}
